import { useSelector } from 'react-redux';
import { Queue } from '@rossum/api-client/queues';
import { Stack } from '@rossum/ui/material';
import { GridFilterItem } from '@rossum/ui/x-data-grid-pro';
import { camelToSnake } from '../../../lib/keyConvertor';
import { canUserPurgeSelector } from '../../../redux/modules/user/selectors';
import { getExportMutationState } from '../annotation-actions/config';
import { usePermittedActions } from '../annotation-actions/hooks/usePermittedActions';
import { useStatusTabs } from '../statuses/hooks/useStatusTabs';
import { getMatchingStatusFilter } from '../statuses/utils';
import { ExportCTA } from './ExportCTA';
import { PurgeCTA } from './PurgeCTA';
import { StartReviewingCTA } from './StartReviewingCTA';
import { DocumentListQuery } from './useStartReviewing';
import {
  getExistingStatusFilter,
  normalizeAnnotations,
  NormalizedAnnotations,
  reviewableStatuses,
} from './utils';

type Props = {
  activeQueue: Queue;
  existingFilterItems: GridFilterItem[];
  annotations: NormalizedAnnotations;
  documentListQuery: DocumentListQuery;
};

export const CTAButtons = ({
  activeQueue,
  existingFilterItems,
  annotations,
  documentListQuery,
}: Props) => {
  const { ids: annotationIds, statuses } = normalizeAnnotations(annotations);

  const statusTabs = useStatusTabs(activeQueue);

  const activeStatusTab = getMatchingStatusFilter({
    existingFilterItems,
    statusTabs,
  });

  const existingStatuses =
    getExistingStatusFilter(existingFilterItems) ?? statuses;

  const { permittedActions } = usePermittedActions();

  const shouldAllowReview = activeStatusTab
    ? activeStatusTab.key === 'reviews' || activeStatusTab.key === 'postponed'
    : false;

  const shouldAllowExport = permittedActions.includes('export');

  const showExportOnTab = activeStatusTab
    ? activeStatusTab.key === 'confirmed' || activeStatusTab.key === 'exports'
    : false;

  const canUserPurge = useSelector(canUserPurgeSelector);

  const shouldAllowPurge =
    activeStatusTab && activeStatusTab.key === 'deleted' && canUserPurge;

  const queueExportSettings: Record<string, boolean> = {
    [activeQueue.url]: activeQueue.settings.hideExportButton !== true,
  };

  const exportMutationState = getExportMutationState(
    annotations,
    queueExportSettings
  );

  // this condition is here because otherwise parent Stack adds spacing to an empty div
  const shouldRender =
    shouldAllowPurge ||
    (shouldAllowExport && showExportOnTab) ||
    shouldAllowReview;

  return shouldRender ? (
    <Stack direction="row" spacing={2}>
      {shouldAllowReview ? (
        <StartReviewingCTA
          queueId={activeQueue.id}
          documentListQuery={documentListQuery}
          existingStatuses={existingStatuses}
          annotationUrls={annotations
            .filter(({ status }) =>
              reviewableStatuses.some(
                reviewableStatus => reviewableStatus === camelToSnake(status)
              )
            )
            .map(({ url }) => url)}
        />
      ) : null}
      {shouldAllowExport && showExportOnTab ? (
        <ExportCTA
          annotations={annotations}
          queueName={activeQueue.name}
          exportMutationState={exportMutationState}
        />
      ) : null}
      {shouldAllowPurge ? (
        <PurgeCTA queueUrl={activeQueue.url} annotationIds={annotationIds} />
      ) : null}
    </Stack>
  ) : null;
};
